import React, { useEffect, useState, useCallback } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaceIcon from '@mui/icons-material/Place';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Title from '../../components/Title';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import {
  getTouristPlacesAsync,
  deleteTouristPlaceAsync,
  selectTouristPlaces,
} from '../../redux/slices/touristPlacesSlice';
import { TouristPlace } from '../../resources/types/touristPlacesTypes';
import TouristPlaceModal from '../../modals/TouristPlaceModal';

const PREFIX = 'TouristPlacesPage';

const classes = {
  header: `${PREFIX}-header`,
  addButton: `${PREFIX}-addButton`,
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  [`& .${classes.addButton}`]: {
    backgroundColor: '#4ECDC4',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#3BA99F',
    },
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(78, 205, 196, 0.08)' 
      : 'rgba(78, 205, 196, 0.04)',
  },
}));

const TableHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(78, 205, 196, 0.1)' 
    : 'rgba(78, 205, 196, 0.05)',
  color: theme.palette.mode === 'dark' ? '#4ECDC4' : '#3BA99F',
}));

const PhotoThumbnail = styled('img')({
  width: 50,
  height: 50,
  objectFit: 'cover',
  borderRadius: 4,
  marginRight: 4,
});

const TouristPlacesPage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const admins = useSelector(selectAdmins);
  const { touristPlaces, loading } = useSelector(selectTouristPlaces);
  const currentCityId = admins.currentCity ?? '';

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<TouristPlace | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [placeToDelete, setPlaceToDelete] = useState<TouristPlace | null>(null);
  const [deleteCountdown, setDeleteCountdown] = useState(3);

  useEffect(() => {
    dispatch(getTouristPlacesAsync());
  }, [dispatch]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (deleteDialogOpen && deleteCountdown > 0) {
      timer = setTimeout(() => setDeleteCountdown(prev => prev - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [deleteDialogOpen, deleteCountdown]);

  const filteredPlaces = touristPlaces.filter(
    place => place.city === currentCityId
  );

  const handleOpenModal = useCallback((place?: TouristPlace) => {
    setSelectedPlace(place || null);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedPlace(null);
  }, []);

  const handleOpenDeleteDialog = useCallback((place: TouristPlace) => {
    setPlaceToDelete(place);
    setDeleteCountdown(3);
    setDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setPlaceToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (placeToDelete?._id) {
      dispatch(deleteTouristPlaceAsync(placeToDelete._id, () => {
        handleCloseDeleteDialog();
      }));
    }
  }, [dispatch, placeToDelete, handleCloseDeleteDialog]);

  const getPhotoUrl = (filename: string) => {
    if (!filename) return '';
    return `https://whee.pro/public/${filename}`;
  };

  return (
    <Root>
      <Box className={classes.header}>
        <Title>Туристичні місця</Title>
        <Button
          className={classes.addButton}
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal()}
        >
          Додати місце
        </Button>
      </Box>

      <Paper
        sx={{
          border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress sx={{ color: '#4ECDC4' }} />
          </Box>
        ) : filteredPlaces.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <PlaceIcon sx={{ fontSize: 64, color: 'rgba(78, 205, 196, 0.3)', mb: 2 }} />
            <Typography variant="h6" color="textSecondary">
              Немає туристичних місць
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Додайте перше туристичне місце для цього міста
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModal()}
              sx={{ borderColor: '#4ECDC4', color: '#4ECDC4' }}
            >
              Додати місце
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Фото</TableHeaderCell>
                  <TableHeaderCell>Назва</TableHeaderCell>
                  <TableHeaderCell>Опис</TableHeaderCell>
                  <TableHeaderCell>Контакти</TableHeaderCell>
                  <TableHeaderCell>Години роботи</TableHeaderCell>
                  <TableHeaderCell align="right">Дії</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPlaces.map(place => (
                  <StyledTableRow key={place._id}>
                    <TableCell>
                      <Box sx={{ display: 'flex' }}>
                        {place.photos?.slice(0, 3).map((photo, idx) => (
                          <PhotoThumbnail
                            key={idx}
                            src={getPhotoUrl(photo)}
                            alt={`${place.name} ${idx + 1}`}
                            onError={(e: any) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ))}
                        {(!place.photos || place.photos.length === 0) && (
                          <Box
                            sx={{
                              width: 50,
                              height: 50,
                              borderRadius: 1,
                              bgcolor: 'rgba(78, 205, 196, 0.1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <PlaceIcon sx={{ color: '#4ECDC4' }} />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {place.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        [{place.coordinates?.[1]?.toFixed(4)}, {place.coordinates?.[0]?.toFixed(4)}]
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {typeof place.description === 'string' 
                          ? (place.description || '-') 
                          : (place.description?.en || place.description?.ua || '-')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {place.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          <PhoneIcon sx={{ fontSize: 14, mr: 0.5, color: '#4ECDC4' }} />
                          <Typography variant="caption">{place.phone}</Typography>
                        </Box>
                      )}
                      {place.website && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LanguageIcon sx={{ fontSize: 14, mr: 0.5, color: '#4ECDC4' }} />
                          <Typography
                            variant="caption"
                            component="a"
                            href={place.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: '#4ECDC4', textDecoration: 'none' }}
                          >
                            {place.website.replace(/^https?:\/\//, '').slice(0, 20)}...
                          </Typography>
                        </Box>
                      )}
                      {!place.phone && !place.website && '-'}
                    </TableCell>
                    <TableCell>
                      {place.workingHours ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AccessTimeIcon sx={{ fontSize: 14, mr: 0.5, color: '#4ECDC4' }} />
                          <Typography variant="body2">{place.workingHours}</Typography>
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenModal(place)}
                        sx={{ color: '#4ECDC4' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteDialog(place)}
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <TouristPlaceModal
        open={modalOpen}
        onClose={handleCloseModal}
        place={selectedPlace}
        cityId={currentCityId}
      />

      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Видалити туристичне місце?</DialogTitle>
        <DialogContent>
          <Typography>
            Ви впевнені, що хочете видалити "{placeToDelete?.name}"?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Цю дію неможливо скасувати.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Скасувати</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteCountdown > 0 || loading}
          >
            {deleteCountdown > 0 ? `Видалити (${deleteCountdown})` : 'Видалити'}
          </Button>
        </DialogActions>
      </Dialog>
    </Root>
  );
};

export default TouristPlacesPage;
